import React from "react";
import { Modal, Button, Spin } from "antd";
import {
  ExclamationCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import useBodyScrollLock from "../../../../hooks/useBodyScrollLock";
import "./ConfirmModal.scss";

/**
 * Confirmation Modal Component
 * Modern modal for confirming dangerous user actions like permanent deletion
 *
 * @param {boolean} visible - Controls modal visibility
 * @param {string} title - Modal title
 * @param {string} message - Confirmation message
 * @param {Function} onConfirm - Callback when confirmed
 * @param {Function} onCancel - Callback when cancelled
 * @param {boolean} loading - Loading state for confirm button
 * @param {string} confirmText - Text for confirm button (default: "Yes, Delete")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} type - Modal type: 'danger' or 'warning' (default: 'danger')
 */
const ConfirmModal = ({
  visible,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  onConfirm,
  onCancel,
  loading = false,
  confirmText = "Yes, Delete",
  cancelText = "Cancel",
  type = "danger",
}) => {
  // Lock body scroll when modal is open
  useBodyScrollLock(visible);

  return (
    <Modal
      open={visible}
      onCancel={loading ? undefined : onCancel}
      footer={null}
      className={`confirm-modal ${type}-modal`}
      width={460}
      centered={true}
      closeIcon={<CloseOutlined className="close-icon" />}
      closable={!loading}
      maskClosable={!loading}
      destroyOnClose={true}
      transitionName=""
      maskTransitionName=""
      getContainer={false}
    >
      {/* Modal Content */}
      <div className="confirm-modal-content">
        {/* Modern Loading Overlay */}
        {loading && (
          <div className="modern-loading-overlay">
            <div className="loading-content">
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 48, color: "#ff4d4f" }}
                    spin
                  />
                }
              />
              <div className="loading-text">
                <h4>Processing...</h4>
                <p>Removing user from system</p>
              </div>
            </div>
          </div>
        )}

        {/* Icon */}
        <div className={`confirm-icon ${type}-icon`}>
          <ExclamationCircleOutlined />
        </div>

        {/* Title & Message */}
        <div className="confirm-text">
          <h3 className="confirm-title">{title}</h3>
          <p className="confirm-message">{message}</p>
        </div>

        {/* Action Buttons */}
        <div className="confirm-actions">
          <Button
            size="large"
            onClick={onCancel}
            disabled={loading}
            className="cancel-btn"
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={onConfirm}
            loading={loading}
            className={`confirm-btn ${type}-btn`}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
